/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  const gateSelector = '[data-size-gate]';

  function getProductForm(element) {
    return element.closest('.product-form');
  }

  function getSizeSelectors(gate) {
    return getProductForm(gate)?.querySelectorAll('.product-form__option-selector[data-option-type="size"]') || [];
  }

  function getBuyButtons(gate) {
    return getProductForm(gate)?.querySelector('.product-form__buy-buttons');
  }

  function getCheckedLabel(selector) {
    const checkedInput = selector.querySelector('input:checked');
    return checkedInput?.id ? selector.querySelector(`label[for="${CSS.escape(checkedInput.id)}"]`) : null;
  }

  function syncGate(gate) {
    const isSelected = gate.dataset.sizeSelected === 'true';
    const buyButtons = getBuyButtons(gate);

    gate.hidden = isSelected;
    if (buyButtons) {
      buyButtons.hidden = !isSelected;
    }

    getSizeSelectors(gate).forEach((selector) => {
      selector.toggleAttribute('data-size-gate-pending', !isSelected);

      const value = selector.querySelector('.product-form__option-value');
      if (value) {
        value.textContent = isSelected ? (getCheckedLabel(selector)?.textContent.trim() || value.textContent) : '';
      }
    });
  }

  function initializeGate(gate) {
    if (!gate.dataset.sizeSelected) {
      gate.dataset.sizeSelected = new URLSearchParams(window.location.search).has('variant') ? 'true' : 'false';
    }
    syncGate(gate);
  }

  function initializeAllGates(root = document) {
    root.querySelectorAll(gateSelector).forEach(initializeGate);
  }

  document.addEventListener('click', (event) => {
    const selectSizeButton = event.target.closest('[data-select-size-button]');
    if (selectSizeButton) {
      const gate = selectSizeButton.closest(gateSelector);
      const selector = getSizeSelectors(gate)[0];
      if (selector) {
        selector.scrollIntoView({ behavior: 'smooth', block: 'center' });
        selector.classList.remove('size-gate-shake');
        requestAnimationFrame(() => selector.classList.add('size-gate-shake'));
        window.setTimeout(() => selector.classList.remove('size-gate-shake'), 600);
      }
      return;
    }

    const sizeChoice = event.target.closest('.product-form__option-selector[data-option-type="size"] label, .product-form__option-selector[data-option-type="size"] [role="option"], .product-form__option-selector[data-option-type="size"] a');
    if (sizeChoice) {
      const gate = getProductForm(sizeChoice)?.querySelector(gateSelector);
      if (gate) {
        gate.dataset.sizeSelected = 'true';
        syncGate(gate);
      }
    }
  });

  document.addEventListener('change', (event) => {
    const sizeSelector = event.target.closest('.product-form__option-selector[data-option-type="size"]');
    if (!sizeSelector) {
      return;
    }

    const gate = getProductForm(sizeSelector)?.querySelector(gateSelector);
    if (gate) {
      gate.dataset.sizeSelected = 'true';
      syncGate(gate);
    }
  });

  document.addEventListener('product:rerender', (event) => {
    const gate = getProductForm(event.target)?.querySelector(gateSelector);
    if (gate) {
      requestAnimationFrame(() => syncGate(gate));
    }
  }, true);

  initializeAllGates();

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) {
          return;
        }

        if (node.matches(gateSelector)) {
          initializeGate(node);
        }
        initializeAllGates(node);

        const gate = node.closest('.product-form')?.querySelector(gateSelector);
        if (gate) {
          syncGate(gate);
        }
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
